import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { User, UserRole } from '../../types';

interface UserState {
  isAuthenticated: boolean;
  currentUser: User | null;
  permissions: {
    canManageUsers: boolean;
    canAccessUsers: boolean;
    canManageManufacturers: boolean;
    canAccessManufacturers: boolean;
    canManageDealers: boolean;
    canAccessDealers: boolean;
    canManageTechnicians: boolean;
    canAccessTechnicians: boolean;
    canManageOrders: boolean;
    canAccessOrders: boolean;
    canManageShippingLocations: boolean;
    canAccessPaintData: boolean;
  };
}

const initialState: UserState = {
  isAuthenticated: false,
  currentUser: null,
  permissions: {
    canManageUsers: false,
    canAccessUsers: false,
    canManageManufacturers: false,
    canAccessManufacturers: false,
    canManageDealers: false,
    canAccessDealers: false,
    canManageTechnicians: false,
    canAccessTechnicians: false,
    canManageOrders: false,
    canAccessOrders: false,
    canManageShippingLocations: false,
    canAccessPaintData: false,
  },
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentUser: (state: UserState, action: PayloadAction<User | null>) => {
      state.currentUser = action.payload;
      state.isAuthenticated = action.payload !== null;
      if (action.payload !== null) {
        state.permissions.canManageUsers =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR;

        state.permissions.canAccessUsers =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.CSR;

        state.permissions.canManageManufacturers = action.payload.role === UserRole.ADMINISTRATOR;
        state.permissions.canAccessManufacturers =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.CSR;

        state.permissions.canManageDealers =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR;

        state.permissions.canAccessDealers =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.CSR;

        state.permissions.canManageTechnicians =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR;

        state.permissions.canAccessTechnicians =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.CSR;

        state.permissions.canManageOrders =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.DEALER;

        state.permissions.canAccessOrders =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.CSR ||
          action.payload.role === UserRole.DEALER;

        state.permissions.canManageShippingLocations =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR;

        state.permissions.canAccessPaintData =
          action.payload.role === UserRole.ADMINISTRATOR ||
          action.payload.role === UserRole.SUPERVISOR ||
          action.payload.role === UserRole.CSR;
      }
    },
    logout: state => {
      state.currentUser = null;
      state.isAuthenticated = false;
      state.permissions = {
        canManageUsers: false,
        canAccessUsers: false,
        canAccessManufacturers: false,
        canManageManufacturers: false,
        canManageDealers: false,
        canAccessDealers: false,
        canManageTechnicians: false,
        canAccessTechnicians: false,
        canManageOrders: false,
        canAccessOrders: false,
        canManageShippingLocations: false,
        canAccessPaintData: false,
      };
    },
  },
});

export const { setCurrentUser, logout } = userSlice.actions;

// Selectors
export const selectCurrentUser = (state: RootState) => state.user.currentUser;
export const selectManageUsers = (state: RootState) => state.user.permissions.canManageUsers;
export const selectAccessUsers = (state: RootState) => state.user.permissions.canAccessUsers;
export const selectAccessManufacturers = (state: RootState) =>
  state.user.permissions.canAccessManufacturers;
export const selectManageManufacturers = (state: RootState) =>
  state.user.permissions.canManageManufacturers;
export const selectManageDealers = (state: RootState) => state.user.permissions.canManageDealers;
export const selectAccessDealers = (state: RootState) => state.user.permissions.canAccessDealers;
export const selectManageTechnicians = (state: RootState) =>
  state.user.permissions.canManageTechnicians;
export const selectAccessTechnicians = (state: RootState) =>
  state.user.permissions.canAccessTechnicians;
export const selectManageOrders = (state: RootState) => state.user.permissions.canManageOrders;
export const selectAccessOrders = (state: RootState) => state.user.permissions.canAccessOrders;
export const selectManageShippingLocations = (state: RootState) =>
  state.user.permissions.canManageShippingLocations;
export const selectAccessPaintData = (state: RootState) =>
  state.user.permissions.canAccessPaintData;

export default userSlice.reducer;
